import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  BarChart3, Receipt, ClipboardList, TestTube, Scan, Pill, RefreshCw, HeartHandshake, CalendarDays,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { extractErrorMessage } from '../../utils/extractErrorMessage';

// PER-SERVICE REPORTS (Phase 4, #16) — one registry-driven screen over the
// composable metrics engine (/api/assistant/reports/metrics?source=…). Each
// enabled service contributes a tab; the tab declares which metrics and
// groupings make sense, the engine does the SQL and the tenancy. Adding a
// metric server-side is one registry row — this page renders whatever comes
// back.

const TEAL = '#4ABDBD';
const DEEP = '#2D8A8A';
const GOLD = '#D7A05C';
const SAGE = '#9DB3B1';
const SERIES_COLORS = [TEAL, GOLD, DEEP, SAGE, '#C7D6D5'];

// Which tabs exist, gated by the hospital's features (the server re-checks).
const TABS = [
  {
    // CONSULTATIONS — the one report a Medi-Consult hospital has (owner, 20
    // Oct 2026: the Pro-App had this tab and the web did not, so tenant 1's
    // web page was empty). Same source, metrics and groupings as the app.
    id: 'appointments', labelKey: 'reports.consults', fallback: 'Consults', Icon: CalendarDays, feature: 'appointments',
    metrics: 'appointments,completed,cancelled,no_shows',
    dims: [
      { id: 'day', labelKey: 'reports.by_day', fallback: 'By day' },
      { id: 'month', labelKey: 'reports.by_month', fallback: 'By month' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
      { id: 'specialty', labelKey: 'reports.by_specialty', fallback: 'By specialty' },
    ],
  },
  {
    // 30 Sep 2026: bills are raised only by pharmacy, lab, radiology and care
    // visits — a consultation's fee lives on the appointment, not on a bill.
    // A consult-only hospital would get a Billing tab that can only be empty.
    id: 'billing', labelKey: 'reports.billing', fallback: 'Billing', Icon: Receipt, feature: ['pharmacy', 'lab', 'radiology', 'care_visits'],
    metrics: 'bills,billed,collected,outstanding',
    dims: [
      { id: 'day', labelKey: 'reports.by_day', fallback: 'By day' },
      { id: 'month', labelKey: 'reports.by_month', fallback: 'By month' },
      { id: 'source', labelKey: 'reports.by_service', fallback: 'By service' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
    ],
  },
  {
    // The service_orders table spans pharmacy/lab/radiology (there's no
    // single tenant feature for it) — show the tab only when at least one of
    // those three is actually on, so a medi_consult-only tenant (which never
    // writes an order row) doesn't see an empty/irrelevant "Orders" tab.
    id: 'service_orders', labelKey: 'reports.orders', fallback: 'Orders', Icon: ClipboardList, feature: ['pharmacy', 'lab', 'radiology'],
    metrics: 'orders,fulfilled,cancelled,stat_orders',
    dims: [
      { id: 'day', labelKey: 'reports.by_day', fallback: 'By day' },
      { id: 'service', labelKey: 'reports.by_service', fallback: 'By service' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
      { id: 'priority', labelKey: 'reports.by_priority', fallback: 'By priority' },
    ],
  },
  {
    id: 'lab_tat', labelKey: 'reports.lab_tat', fallback: 'Lab turnaround', Icon: TestTube, feature: 'lab',
    metrics: 'reports,released,avg_tat_hours',
    dims: [
      { id: 'day', labelKey: 'reports.by_day', fallback: 'By day' },
      { id: 'month', labelKey: 'reports.by_month', fallback: 'By month' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
    ],
  },
  {
    id: 'radiology_studies', labelKey: 'reports.radiology', fallback: 'Radiology', Icon: Scan, feature: 'radiology',
    metrics: 'studies,performed,cancelled,avg_wait_hours',
    dims: [
      { id: 'day', labelKey: 'reports.by_day', fallback: 'By day' },
      { id: 'modality', labelKey: 'reports.by_modality', fallback: 'By modality' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
    ],
  },
  {
    id: 'care_visits', labelKey: 'reports.care', fallback: 'Home care', Icon: HeartHandshake, feature: 'care_visits',
    metrics: 'visits,completed,cancelled,care_hours',
    dims: [
      { id: 'day', labelKey: 'reports.by_day', fallback: 'By day' },
      { id: 'month', labelKey: 'reports.by_month', fallback: 'By month' },
      { id: 'caretaker', labelKey: 'reports.by_caretaker', fallback: 'By care taker' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
    ],
  },
  {
    id: 'pharmacy_stock', labelKey: 'reports.stock', fallback: 'Stock & expiry', Icon: Pill, feature: 'pharmacy',
    metrics: 'batches,units,stock_value,expiring_90d,expired',
    snapshot: true, // current stock — no date range
    dims: [
      { id: 'store', labelKey: 'reports.by_store', fallback: 'By store' },
      { id: 'expiry_month', labelKey: 'reports.by_expiry', fallback: 'By expiry month' },
      { id: 'status', labelKey: 'reports.by_status', fallback: 'By status' },
    ],
  },
];

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const ServiceReports = () => {
  const { t, i18n } = useTranslation();
  const { has: hasFeature } = useTenantFeatures();

  const tabs = useMemo(() => TABS.filter((tab) => {
    if (!tab.feature) return true;
    // A tab can name one feature (string) or, like service_orders which spans
    // pharmacy/lab/radiology, a list — visible if ANY of them is on.
    return Array.isArray(tab.feature) ? tab.feature.some(hasFeature) : hasFeature(tab.feature);
  }), [hasFeature]);
  const [tabId, setTabId] = useState(tabs[0]?.id || 'appointments');
  const tab = tabs.find((x) => x.id === tabId) || tabs[0];

  const [dim, setDim] = useState(tab?.dims[0]?.id || 'day');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Switching tab resets the grouping to that tab's first sensible one.
  useEffect(() => { if (tab) setDim(tab.dims[0].id); }, [tabId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    // No service is on (a consult-only hospital): no tab, nothing to load —
    // settle on the empty state rather than a skeleton that never ends.
    if (!tab) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const axiosInstance = createAuthedAxios();
      const qs = new URLSearchParams({ source: tab.id, metrics: tab.metrics, dimensions: dim });
      if (!tab.snapshot) { qs.set('from', from); qs.set('to', to); }
      const { data: res } = await axiosInstance.get(`${baseURL}/assistant/reports/metrics?${qs.toString()}`);
      setData(res.data);
    } catch (err) {
      setData(null);
      // A refusal's `message` can be { en, fr } — never hand the object to a toast.
      toast.error(extractErrorMessage(err, t('reports.load_failed', 'Could not load the report'), (i18n.language || 'en').slice(0, 2)));
    } finally {
      setLoading(false);
    }
  }, [tab, dim, from, to, t, i18n.language]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const columns = data?.columns || [];
  const xKey = columns[0];
  const metricKeys = columns.slice(1);
  const timeChart = ['day', 'month'].includes(dim);
  const ChartComp = timeChart ? LineChart : BarChart;

  // No report of this page applies (no consultations and no service on): the
  // sidebar has no entry (owner, 20 Oct 2026), and a typed URL lands home
  // rather than on a page that can only be empty. `has` answers true until
  // the features load, so an empty list here is a real answer.
  if (tabs.length === 0) return <Navigate to="/" replace />;

  return (
    <div data-testid="service-reports-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md">
            <BarChart3 size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">{t('reports.service_title', 'Service reports')}</h1>
            <p className="text-xs text-gray-400">{t('reports.service_sub', 'Billing, orders, turnaround, modality and stock')}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!tab?.snapshot && (
            <>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all" data-testid="reports-from" />
              <span className="text-gray-300">–</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all" data-testid="reports-to" />
            </>
          )}
          <select value={dim} onChange={(e) => setDim(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all cursor-pointer" data-testid="reports-dimension">
            {tab?.dims.map((d) => <option key={d.id} value={d.id}>{t(d.labelKey, d.fallback)}</option>)}
          </select>
          <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer" title={t('common.refresh', 'Refresh')}>
            <RefreshCw size={17} />
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTabId(tb.id)}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap flex items-center gap-1.5 transition-colors cursor-pointer ${tab?.id === tb.id ? 'bg-[#2D8A8A] text-white shadow-md' : 'bg-white text-gray-500 hover:text-gray-700 shadow-sm'}`}
            data-testid={`reports-tab-${tb.id}`}
          >
            <tb.Icon size={15} />
            {t(tb.labelKey, tb.fallback)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse h-20 bg-white rounded-2xl shadow-sm" />)}
          </div>
          <div className="animate-pulse h-72 bg-white rounded-2xl shadow-sm" />
        </div>
      ) : !data ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <BarChart3 size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-500">{t('reports.nothing', 'No report data.')}</p>
        </div>
      ) : (
        <>
          {data.summary && Object.keys(data.summary).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {Object.entries(data.summary).map(([k, v]) => (
                <div key={k} className="bg-white rounded-2xl shadow-md p-4">
                  <p className="text-[11px] text-gray-400 mb-1 capitalize">{k.replaceAll('_', ' ')}</p>
                  <p className="text-lg font-bold text-gray-800">
                    {Number(v) % 1 === 0 ? Number(v).toLocaleString() : Number(v).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-md p-5 mb-5">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-10">{t('reports.empty_range', 'Nothing in this range.')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <ChartComp data={rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f1" />
                  <XAxis dataKey={xKey} tick={{ fontSize: 11 }} stroke="#9DB3B1" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9DB3B1" />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {metricKeys.map((m, i) => timeChart
                    ? <Line key={m} type="monotone" dataKey={m} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={false} />
                    : <Bar key={m} dataKey={m} fill={SERIES_COLORS[i % SERIES_COLORS.length]} radius={[6, 6, 0, 0]} />)}
                </ChartComp>
              </ResponsiveContainer>
            )}
          </div>

          {rows.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      {columns.map((c) => (
                        <th key={c} className="px-5 py-3 font-medium capitalize">{String(c).replaceAll('_', ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                        {columns.map((c) => (
                          <td key={c} className="px-5 py-3 text-gray-700">
                            {typeof r[c] === 'number' && r[c] % 1 !== 0 ? r[c].toFixed(2) : String(r[c] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ServiceReports;
